import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { User, Workspace, WorkspaceGroup, ImpactDomain, Incident, Task, Notification } from '../types';
import { authService, MyWorkspaceSummary } from '../services/authService';
import { api, tokenStorage, setOnUnauthorized } from '../lib/api';
import { connectSocket, disconnectSocket } from '../lib/socket';

// Connects the socket, wires its notification push into the notification
// store, and fetches the current list so a page refresh isn't empty until
// the next push arrives. Called from both login and bootstrap (the two
// places a session becomes valid) — kept here instead of duplicated in
// each so there's one place that defines "what happens when we're signed in".
function connectSocketAndSubscribe() {
  const socket = connectSocket();
  socket?.on('notification_received', (payload: { notification: Notification }) => {
    useNotificationStore.getState().addNotification(payload.notification);
    useToastStore.getState().pushToast(payload.notification);
  });
  useNotificationStore.getState().fetchNotifications();
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isBootstrapping: boolean;
  login: (email: string, password: string) => Promise<void>;
  googleLogin: (idToken: string) => Promise<void>;
  acceptInvite: (token: string, name: string, password: string) => Promise<void>;
  acceptProjectInvite: (token: string, name: string, password: string) => Promise<void>;
  verifyEmail: (token: string) => Promise<void>;
  logout: () => Promise<void>;
  bootstrap: () => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  devtools(
    persist(
      (set) => ({
        user: null,
        isAuthenticated: false,
        isBootstrapping: true,

        login: async (email, password) => {
          const user = await authService.login(email, password);
          set({ user, isAuthenticated: true });
          connectSocketAndSubscribe();
        },

        googleLogin: async (idToken) => {
          const user = await authService.googleLogin(idToken);
          set({ user, isAuthenticated: true });
          connectSocketAndSubscribe();
        },

        // Accepting an invite or verifying an email both complete a sign-in
        // the same way login does (backend returns a token pair) — same
        // post-session side effects apply.
        acceptInvite: async (token, name, password) => {
          const user = await authService.acceptInvite(token, name, password);
          set({ user, isAuthenticated: true });
          connectSocketAndSubscribe();
        },

        acceptProjectInvite: async (token, name, password) => {
          const user = await authService.acceptProjectInvite(token, name, password);
          set({ user, isAuthenticated: true });
          connectSocketAndSubscribe();
        },

        verifyEmail: async (token) => {
          const user = await authService.verifyEmail(token);
          set({ user, isAuthenticated: true });
          connectSocketAndSubscribe();
        },

        logout: async () => {
          await authService.logout().catch(() => {});
          disconnectSocket();
          set({ user: null, isAuthenticated: false });
          useNotificationStore.getState().reset();
          useToastStore.getState().clearToasts();
        },

        // Called once on app start: if a token is stored, validate it
        // against the API and refresh the user object. Clears stale auth
        // state if the token is no longer valid.
        bootstrap: async () => {
          if (!tokenStorage.getAccessToken()) {
            set({ isBootstrapping: false });
            return;
          }
          try {
            const user = await authService.getMe();
            set({ user, isAuthenticated: true, isBootstrapping: false });
            connectSocketAndSubscribe();
          } catch {
            tokenStorage.clearTokens();
            set({ user: null, isAuthenticated: false, isBootstrapping: false });
          }
        }
      }),
      { name: 'auth-storage', partialize: (state) => ({ user: state.user, isAuthenticated: state.isAuthenticated }) }
    )
  )
);

// Wire the API client's 401-after-refresh-failed handler to log the user
// out in the store, without creating a circular import between lib/api.ts
// and this file.
setOnUnauthorized(() => {
  useAuthStore.setState({ user: null, isAuthenticated: false });
});

interface WorkspaceState {
  workspace: Workspace | null;
  groups: WorkspaceGroup[];
  domains: ImpactDomain[];
  members: User[];
  myWorkspaces: MyWorkspaceSummary[];
  setWorkspace: (workspace: Workspace | null) => void;
  setGroups: (groups: WorkspaceGroup[]) => void;
  setDomains: (domains: ImpactDomain[]) => void;
  setMembers: (members: User[]) => void;
  setMyWorkspaces: (workspaces: MyWorkspaceSummary[]) => void;
  // Flips the active workspace, then hard-reloads the page. A full reload
  // (rather than an in-place store refresh) is deliberate for v1: almost
  // nothing in the app keys its data fetches on workspace id today (there
  // was never a reason to before multi-workspace membership), so reloading
  // is the only way to guarantee no stale cross-workspace data lingers
  // anywhere after a switch.
  switchWorkspace: (workspaceId: string) => Promise<void>;
}

export const useWorkspaceStore = create<WorkspaceState>()(
  devtools((set) => ({
    workspace: null,
    groups: [],
    domains: [],
    members: [],
    myWorkspaces: [],
    setWorkspace: (workspace) => set({ workspace }),
    setGroups: (groups) => set({ groups }),
    setDomains: (domains) => set({ domains }),
    setMembers: (members) => set({ members }),
    setMyWorkspaces: (myWorkspaces) => set({ myWorkspaces }),
    switchWorkspace: async (workspaceId) => {
      await authService.switchWorkspace(workspaceId);
      window.location.reload();
    }
  }))
);

interface IncidentState {
  incidents: Incident[];
  currentIncident: Incident | null;
  setIncidents: (incidents: Incident[]) => void;
  upsertIncident: (incident: Incident) => void;
  setCurrentIncident: (incident: Incident | null) => void;
}

export const useIncidentStore = create<IncidentState>()(
  devtools((set) => ({
    incidents: [],
    currentIncident: null,
    setIncidents: (incidents) => set({ incidents }),
    upsertIncident: (incident) =>
      set((state) => {
        const exists = state.incidents.some((i) => i.id === incident.id);
        return {
          incidents: exists
            ? state.incidents.map((i) => (i.id === incident.id ? incident : i))
            : [incident, ...state.incidents],
          currentIncident: state.currentIncident?.id === incident.id ? incident : state.currentIncident
        };
      }),
    setCurrentIncident: (incident) => set({ currentIncident: incident })
  }))
);

interface TaskState {
  tasks: Task[];
  setTasks: (tasks: Task[]) => void;
  upsertTask: (task: Task) => void;
}

export const useTaskStore = create<TaskState>()(
  devtools((set) => ({
    tasks: [],
    setTasks: (tasks) => set({ tasks }),
    upsertTask: (task) =>
      set((state) => {
        const exists = state.tasks.some((t) => t.id === task.id);
        return {
          tasks: exists ? state.tasks.map((t) => (t.id === task.id ? task : t)) : [task, ...state.tasks]
        };
      })
  }))
);

interface NotificationState {
  notifications: Notification[];
  unreadCount: number;
  fetchNotifications: () => Promise<void>;
  addNotification: (notification: Notification) => void;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  clearAll: () => Promise<void>;
  reset: () => void;
}

export const useNotificationStore = create<NotificationState>()(
  devtools((set, get) => ({
    notifications: [],
    unreadCount: 0,

    fetchNotifications: async () => {
      const [list, count] = await Promise.all([
        api.get<{ data: Notification[] }>('/notifications'),
        api.get<{ data: { count: number } }>('/notifications/unread-count')
      ]);
      set({ notifications: list.data.data, unreadCount: count.data.data.count });
    },

    // Pushed by the socket in real time — prepend rather than refetch.
    addNotification: (notification) =>
      set((state) => ({
        notifications: [notification, ...state.notifications],
        unreadCount: state.unreadCount + 1
      })),

    markAsRead: async (id) => {
      await api.put(`/notifications/${id}/read`);
      set((state) => ({
        notifications: state.notifications.map((n) => (n.id === id ? { ...n, is_read: true } : n)),
        unreadCount: Math.max(0, state.unreadCount - (get().notifications.find((n) => n.id === id && !n.is_read) ? 1 : 0))
      }));
    },

    markAllAsRead: async () => {
      await api.put('/notifications/read-all');
      set((state) => ({
        notifications: state.notifications.map((n) => ({ ...n, is_read: true })),
        unreadCount: 0
      }));
    },

    clearAll: async () => {
      await api.delete('/notifications');
      set({ notifications: [], unreadCount: 0 });
    },

    reset: () => set({ notifications: [], unreadCount: 0 })
  }))
);

// Ephemeral toast queue (Faz E) — separate from useNotificationStore's
// persistent list above, which the bell dropdown reads. Keyed by the
// notification's own id (already unique), which makes pushToast
// idempotent and dismissToast trivial.
interface ToastState {
  toasts: Notification[];
  pushToast: (n: Notification) => void;
  dismissToast: (id: string) => void;
  clearToasts: () => void;
}

export const useToastStore = create<ToastState>()(
  devtools((set) => ({
    toasts: [],
    pushToast: (n) =>
      set((state) => {
        if (state.toasts.some((t) => t.id === n.id)) return state;
        // Newest first, capped so a notification burst can't paper over
        // the screen.
        return { toasts: [n, ...state.toasts].slice(0, 4) };
      }),
    dismissToast: (id) => set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) })),
    clearToasts: () => set({ toasts: [] })
  }))
);

interface UIState {
  sidebarOpen: boolean;
  theme: 'light' | 'dark';
  language: 'en' | 'tr' | 'es';
  // Global UI density (comfortable/compact) — like theme/language, a
  // per-browser persisted preference shared across pages (tasks, incidents).
  density: 'comfortable' | 'compact';
  projectDescription: string;
  toggleSidebar: () => void;
  setTheme: (theme: 'light' | 'dark') => void;
  setLanguage: (language: 'en' | 'tr' | 'es') => void;
  setDensity: (density: 'comfortable' | 'compact') => void;
  setProjectDescription: (description: string) => void;
}

export const useUIStore = create<UIState>()(
  devtools(
    persist(
      (set) => ({
        // Open by default only where the sidebar is a permanent column (lg+);
        // on phones/tablets it's an overlay drawer that must start closed.
        sidebarOpen: typeof window === 'undefined' || window.innerWidth >= 1024,
        theme: 'light',
        language: 'en',
        density: 'comfortable',
        projectDescription: 'Etki Alanı yönetim sistemi - Incident ve task yönetimi için merkezi platform.',
        toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),
        setTheme: (theme) => set({ theme }),
        setLanguage: (language) => set({ language }),
        setDensity: (density) => set({ density }),
        setProjectDescription: (description) => set({ projectDescription: description })
      }),
      {
        name: 'ui-storage',
        // sidebarOpen is deliberately NOT persisted: it depends on the current
        // viewport, and a value saved on desktop would re-open the mobile drawer.
        partialize: (state) => ({
          theme: state.theme,
          language: state.language,
          density: state.density,
          projectDescription: state.projectDescription
        }),
        merge: (persisted, current) => ({ ...current, ...(persisted as object), sidebarOpen: current.sidebarOpen })
      }
    )
  )
);
